import { DOMAIN_HEX, hexToRgba } from './domains';

export type DomainGradientPreset = 'card' | 'header';

const PRESETS: Record<
  DomainGradientPreset,
  { alphas: [number, number, number, number]; stops: [number, number, number, number, number] }
> = {
  card: {
    alphas: [0.28, 0.14, 0.10, 0.04],
    stops:  [0,    0.22, 0.42, 0.62, 0.80],
  },
  header: {
    alphas: [0.35, 0.18, 0.12, 0.05],
    stops:  [0,    0.25, 0.45, 0.65, 0.82],
  },
};

const GRADIENT_ANGLE_DEG = 105;

function domainColorPair(domains: string[]): [string, string] | null {
  const cols = domains.slice(0, 2).map((d) => DOMAIN_HEX[d]).filter(Boolean);
  if (cols.length === 0) return null;
  return [cols[0], cols[1] ?? cols[0]];
}

/** CSS `linear-gradient` string for domain tint overlays (DeckCard, headers, live preview). */
export function domainGradientCss(
  domains: string[],
  preset: DomainGradientPreset = 'card',
): string {
  const pair = domainColorPair(domains);
  if (!pair) return 'none';

  const [a, b] = pair;
  const { alphas, stops } = PRESETS[preset];

  return [
    `linear-gradient(${GRADIENT_ANGLE_DEG}deg,`,
    `  ${hexToRgba(a, alphas[0])}  ${stops[0] * 100}%,`,
    `  ${hexToRgba(a, alphas[1])}  ${stops[1] * 100}%,`,
    `  ${hexToRgba(b, alphas[2])}  ${stops[2] * 100}%,`,
    `  ${hexToRgba(b, alphas[3])}  ${stops[3] * 100}%,`,
    `  transparent           ${stops[4] * 100}%`,
    `)`,
  ].join('');
}

/** Map a CSS gradient angle to canvas gradient endpoints inside a rect. */
function cssAngleEndpoints(
  angleDeg: number,
  x: number, y: number, w: number, h: number,
): { x0: number; y0: number; x1: number; y1: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  const cx  = x + w / 2;
  const cy  = y + h / 2;
  const len = (Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad))) / 2;
  return {
    x0: cx - Math.cos(rad) * len,
    y0: cy - Math.sin(rad) * len,
    x1: cx + Math.cos(rad) * len,
    y1: cy + Math.sin(rad) * len,
  };
}

/** Paint the domain tint on a canvas rect (matches `domainGradientCss`). Returns false if no domains. */
export function fillDomainGradientBackground(
  ctx: CanvasRenderingContext2D,
  domains: string[],
  x: number, y: number, w: number, h: number,
  preset: DomainGradientPreset = 'card',
): boolean {
  const pair = domainColorPair(domains);
  if (!pair) return false;

  const [a, b] = pair;
  const { alphas, stops } = PRESETS[preset];
  const { x0, y0, x1, y1 } = cssAngleEndpoints(GRADIENT_ANGLE_DEG, x, y, w, h);

  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  grad.addColorStop(stops[0], hexToRgba(a, alphas[0]));
  grad.addColorStop(stops[1], hexToRgba(a, alphas[1]));
  grad.addColorStop(stops[2], hexToRgba(b, alphas[2]));
  grad.addColorStop(stops[3], hexToRgba(b, alphas[3]));
  grad.addColorStop(stops[4], 'transparent');

  ctx.save();
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  return true;
}
